<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import SkoolProfileLink from '@ui/SkoolProfileLink.svelte';
  import Conversation from '@ui/conversation/Conversation.svelte';
  import type { ConversationView, DmMessageView, Party } from '@ui/conversation/types';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import { redirectIfAwaitingOnboarding, getToken, handle401, timeAgo, type AdminUser } from '../lib/admin';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { fetchDmChannels, fetchDmInboxes, fetchDmMessages, type DmChannelRow, type DmCounterpart, type DmInbox, type DmMessageRow } from '@api';

  // THE DM INBOX EXPLORER — superadmin only.
  //
  // AN INSTRUMENT, NOT A PAGE. It is opened when something is broken, so it is laid out
  // like a mail client and behaves like one: three panes side by side, each scrolling
  // ITSELF, and the document never scrolling at all. The first version stacked them —
  // reading a transcript meant scrolling past a hundred-row table, and the inbox list
  // left the screen entirely on the way. Three independent things were fighting over one
  // scrollbar.
  //
  // NOTHING ANIMATES beyond a colour change on hover. Movement in a debugging tool is
  // noise: every pixel that moves is a pixel you have to re-read.
  //
  // NOT COMMUNITY-SCOPED, unlike every other admin page. A Skool account has ONE inbox
  // spanning every community it touches, so this ignores the community selector rather
  // than pretending the two line up.
  const CHANNEL_PAGE = 100;
  const MESSAGE_PAGE = 200;

  let me = $state<AdminUser | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let inboxes = $state<DmInbox[]>([]);
  let selectedUser = $state<number | null>(null);

  let channels = $state<DmChannelRow[]>([]);
  let channelTotal = $state(0);
  let channelsLoading = $state(false);
  let issuesOnly = $state(false);
  let channelQuery = $state('');
  let channelTimer: ReturnType<typeof setTimeout> | undefined;

  /** The open channel, as `<Conversation>` keys it — its `channel_id` as a string. */
  let openId = $state<string | null>(null);
  const selectedChannel = $derived(openId == null ? null : Number(openId));
  let messages = $state<DmMessageRow[]>([]);
  let messageTotal = $state(0);
  let messagesLoading = $state(false);
  let head = $state<DmChannelRow | null>(null);
  /** Offset of the FIRST message on screen. `> 0` means older ones exist, which is
   *  what the scroll-up loader asks about. */
  let msgOffset = $state(0);

  const nowMs = Date.now();
  const inbox = $derived(inboxes.find((i) => i.user_id === selectedUser) ?? null);

  // --- viewport fit -----------------------------------------------------------------
  // Bound the three panes to what is left below the header and let each scroll inside,
  // rather than growing the document to the height of the longest one. Measured, not a
  // `calc(100dvh - <constant>)`: the strip above is not a fixed height (trial and
  // roster banners appear conditionally), which is the same reason `PipelineBoard`
  // measures rather than hardcodes.
  //
  // Below `lg` the three-pane idea is wrong anyway — a phone gets normal document flow,
  // so the height is left unset and the panes stack.
  const MIN_H = 360;
  const BOTTOM_GAP = 32;
  const WIDE = 1024;

  let paneEl = $state<HTMLDivElement | null>(null);
  let paneH = $state<number | null>(null);

  function measure(): void {
    const el = paneEl;
    if (!el) return;
    if (window.innerWidth < WIDE) {
      paneH = null;
      return;
    }
    paneH = Math.max(MIN_H, window.innerHeight - el.getBoundingClientRect().top - BOTTOM_GAP);
  }

  $effect(() => {
    void inboxes; // re-measure once the panes have content
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  /** Plain English for the API's `issues` codes. Copy lives here; the codes are the
   *  contract. Named for what a reader should DO about it, not for the column that
   *  produced it. */
  const ISSUE_TEXT: Record<string, string> = {
    no_messages: 'no messages stored',
    history_incomplete: 'backfill unfinished',
    never_synced: 'never synced',
    no_watermark: 'no watermark',
    consent_revoked_data_remains: 'revoked, data remains',
    enabled_but_no_channels: 'enabled, nothing arrived',
    channels_but_no_messages: 'channels, no messages',
  };
  function issueText(code: string): string {
    const [name, n] = code.split(':');
    const label = ISSUE_TEXT[name ?? ''] ?? name ?? code;
    return n ? `${label} · ${n}` : label;
  }

  /** How much of an inbox is fully backfilled, 0–100. The one number that answers "is
   *  this healthy" without reading four others. */
  function completeness(row: DmInbox): number {
    return row.channels === 0 ? 0 : Math.round((row.history_complete_channels / row.channels) * 100);
  }

  async function loadInboxes(): Promise<void> {
    inboxes = await fetchDmInboxes();
    if (selectedUser == null && inboxes.length) void selectInbox(inboxes[0]!.user_id);
  }

  async function selectInbox(userId: number): Promise<void> {
    selectedUser = userId;
    openId = null;
    messages = [];
    head = null;
    await loadChannels();
  }

  async function loadChannels(): Promise<void> {
    if (selectedUser == null) return;
    channelsLoading = true;
    try {
      const res = await fetchDmChannels(selectedUser, {
        limit: CHANNEL_PAGE,
        issuesOnly,
        q: channelQuery,
      });
      channels = res.channels;
      channelTotal = res.total;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      channels = [];
    } finally {
      channelsLoading = false;
    }
  }

  /**
   * The search box belongs to `<Conversation>`; what a query MEANS belongs here.
   *
   * Server-side, debounced, unlike the Helper's — that surface holds one account's
   * whole list in memory and narrows it locally, while this one pages a hundred
   * conversations at a time out of six thousand, so searching what happens to be on
   * screen would answer the wrong question.
   */
  $effect(() => {
    const q = channelQuery;
    if (selectedUser == null) return;
    clearTimeout(channelTimer);
    channelTimer = setTimeout(() => void loadChannels(), 300);
    void q;
    return () => clearTimeout(channelTimer);
  });

  /**
   * Open at the END of the conversation, and page backwards — the same reading the
   * Helper's thread gives, because it is the same component now.
   *
   * BY OFFSET ARITHMETIC, because this route is offset-paged and chronological where
   * `/v1/me/dms/…/messages` takes a `before` cursor. The channel row already carries
   * the message count, so the last page is `total - limit`; the response's own `total`
   * is authoritative and corrects the estimate when messages landed between the list
   * being read and the conversation being opened, which is the only way the two
   * disagree.
   */
  async function openChannel(channelId: number, known: number): Promise<void> {
    if (selectedUser == null) return;
    messagesLoading = true;
    messages = [];
    try {
      const at = Math.max(0, known - MESSAGE_PAGE);
      let res = await fetchDmMessages(selectedUser, channelId, {
        limit: MESSAGE_PAGE,
        offset: at,
      });
      if (res.total !== known) {
        const fixed = Math.max(0, res.total - MESSAGE_PAGE);
        if (fixed !== at) {
          res = await fetchDmMessages(selectedUser, channelId, {
            limit: MESSAGE_PAGE,
            offset: fixed,
          });
        }
        msgOffset = fixed;
      } else {
        msgOffset = at;
      }
      if (selectedChannel !== channelId) return;
      messages = res.messages;
      messageTotal = res.total;
      head = res.channel;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      messages = [];
      head = null;
    } finally {
      messagesLoading = false;
    }
  }

  /** Selection lives in `<Conversation>`, so the fetch keys off the id rather than a
   *  click — the same reason the Helper's thread loads from an effect. */
  $effect(() => {
    const id = selectedChannel;
    head = null;
    messages = [];
    msgOffset = 0;
    if (id == null) return;
    const row = channels.find((c) => c.channel_id === id);
    void openChannel(id, row?.messages ?? 0);
  });

  /** The page before the oldest message on screen, prepended. `<Conversation>` holds
   *  the reader's position across the insert. */
  async function loadOlder(): Promise<void> {
    const id = selectedChannel;
    if (id == null || selectedUser == null || msgOffset <= 0) return;
    const at = Math.max(0, msgOffset - MESSAGE_PAGE);
    const res = await fetchDmMessages(selectedUser, id, {
      limit: msgOffset - at,
      offset: at,
    });
    if (selectedChannel !== id) return;
    messages = [...res.messages, ...messages];
    msgOffset = at;
  }

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    try {
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      await loadInboxes();
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load the DM inboxes.';
    } finally {
      loading = false;
    }
  }

  // --- the transcript, in the shape the shared bubble renders --------------------

  /**
   * `direction` → which side, with `unknown` kept as null.
   *
   * That third value is the reason this is a mapping and not a rename: the explorer's
   * job is to show what we actually mirrored, and a message whose sender resolves to
   * neither party is a finding. `DmMessageView.outbound` models it as null and the
   * bubble draws it centred and flagged, where the old markup only tinted it warning
   * and left it on the left — which read as "the counterpart said this".
   */
  function bubble(m: DmMessageRow): DmMessageView {
    return {
      id: String(m.id),
      content: m.content ?? '',
      createdAt: m.created_at,
      outbound: m.direction === 'out' ? true : m.direction === 'in' ? false : null,
      videos: m.videos,
      attachments: m.attachments,
    };
  }

  /** A channel's resolved party as the bubble's avatar. `picture` is not on this route
   *  yet (#164), so these render as initials today and as faces the moment it is —
   *  read rather than hardcoded to null precisely so that needs no release here. No
   *  `memberId`: this page is not community-scoped, so there is no roles map to ring an
   *  avatar against. */
  function party(p: DmCounterpart | null): Party | null {
    if (!p) return null;
    return {
      name: p.name ?? (p.handle ? '@' + p.handle : null),
      pictureUrl: p.picture ?? null,
    };
  }

  /**
   * The channel rows as `<Conversation>` renders them.
   *
   * `preview` carries the COMMUNITY rather than the last message: this list is scanned
   * for "which conversation is broken", and the route does not send a last-message
   * excerpt anyway. `meta` is the mirrored message count, the column a reader runs
   * down looking for the zero.
   */
  const asConversations = $derived<ConversationView[]>(
    channels.map((c) => ({
      id: String(c.channel_id),
      name: c.counterpart.name ?? c.counterpart.handle ?? c.external_id.slice(0, 12),
      handle: c.counterpart.handle,
      pictureUrl: c.counterpart.picture ?? null,
      preview: c.community ?? (c.member_id ? `member ${c.member_id}` : 'unlinked'),
      lastAt: c.last_message_at,
      unread: c.num_unread || 0,
      meta: c.messages.toLocaleString(),
      skoolUrl: c.counterpart.skool_url,
      owner: party(c.owner),
    })),
  );

  const threadMessages = $derived<DmMessageView[]>(messages.map(bubble));

  void init();
</script>

<!-- The transcript's header, replacing the shared component's default.
     That default names WHO you are talking to, which is the question in a side panel
     and is already answered here by the inbox picker and the row you clicked. The
     question on this page is whether the mirror is intact, so the strip carries both
     parties as links out to Skool, the channel's id, its backfill state and its
     watermark — reading a watermark against the last message IS the diagnosis. -->
{#snippet channelHead()}
  <div class="min-w-0 flex-1">
    <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <div class="flex min-w-0 flex-wrap items-baseline gap-x-2 text-sm">
        <SkoolProfileLink
          name={head?.owner?.name ?? null}
          handle={head?.owner?.handle ?? null}
          href={head?.owner?.skool_url ?? null}
          pictureUrl={head?.owner?.picture ?? null}
          tone="owner"
        />
        <span class="text-base-content/35">↔</span>
        <SkoolProfileLink
          name={head?.counterpart?.name ?? null}
          handle={head?.counterpart?.handle ?? null}
          href={head?.counterpart?.skool_url ?? null}
          pictureUrl={head?.counterpart?.picture ?? null}
          tone="counterpart"
        />
      </div>
      <span class="shrink-0 font-mono text-[0.62rem] tabular-nums text-base-content/50">
        {messages.length} / {messageTotal.toLocaleString()} messages
      </span>
    </div>
    <div class="mt-0.5 flex flex-wrap gap-x-3 font-mono text-[0.58rem] text-base-content/45">
      <span>channel {head?.channel_id}</span>
      <span>{head?.history_complete ? 'backfill complete' : 'backfill unfinished'}</span>
      {#if head?.newest_synced_message_external_id}
        <span>watermark {head.newest_synced_message_external_id.slice(0, 10)}</span>
      {/if}
      {#if head?.member_id}
        <a href="/admin/community/members/{head.member_id}" class="text-primary/70 hover:underline">member {head.member_id}</a>
      {/if}
    </div>
  </div>
{/snippet}

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <!-- `overflow-hidden` is what makes the panes the only scrollers on this screen —
         without it the document keeps its own scrollbar and the bounded height below
         just moves the problem.
         LG AND UP ONLY. Below that there is no measured height, the panes stack at their
         natural size, and hiding the overflow here would clip everything past the fold
         with nothing able to scroll to it. -->
    <PageContainer fill class="lg:overflow-hidden">
      <!-- Not in the sidebar (reachable by URL only), but it is a Super admin tool and
           saying so is the point — an unlisted page is the one most in need of telling
           you where you are. The standing caveat is the page's `scope`. -->
      <PageHeader
        section="Super admin"
        title="Inbox explorer"
        scope="Mirrored DM inboxes, per owner — real private messages, open them to debug the mirror, not to read them"
      />

      <div
        bind:this={paneEl}
        class="grid min-h-0 gap-3 lg:grid-cols-[15rem_24rem_minmax(0,1fr)]"
        style={paneH != null ? `height:${paneH}px` : undefined}
      >
        <!-- ═══ 1 · inboxes ═══════════════════════════════════════════════ -->
        <section class="flex min-h-0 flex-col overflow-hidden rounded-box border hair bg-base-100">
          <header class="flex items-baseline justify-between border-b hair px-3 py-2">
            <span class="eyebrow">Inboxes</span>
            <span class="font-mono text-[0.62rem] tabular-nums text-base-content/50">{inboxes.length}</span>
          </header>

          {#if inboxes.length === 0}
            <p class="px-3 py-8 text-center text-sm text-base-content/62">
              Nobody has enabled the DM mirror yet.
            </p>
          {:else}
            <ul class="min-h-0 flex-1 overflow-y-auto">
              {#each inboxes as row (row.user_id)}
                {@const pct = completeness(row)}
                <li>
                  <button
                    class="w-full border-l-2 px-3 py-2.5 text-left transition-colors focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-primary/50 {row.user_id === selectedUser
                      ? 'border-l-primary bg-primary/[0.07]'
                      : 'border-l-transparent hover:bg-base-200/60'}"
                    onclick={() => void selectInbox(row.user_id)}
                    aria-current={row.user_id === selectedUser}
                  >
                    <div class="flex items-baseline gap-2">
                      <span class="redact min-w-0 flex-1 truncate text-sm font-medium">
                        {row.name || row.email || `user ${row.user_id}`}
                      </span>
                      {#if !row.enabled}
                        <span class="shrink-0 text-[0.55rem] uppercase tracking-wide text-base-content/45">off</span>
                      {/if}
                    </div>

                    <div class="mt-0.5 font-mono text-[0.6rem] tabular-nums text-base-content/55">
                      {row.messages.toLocaleString()} msgs · {row.channels} channels
                    </div>

                    <!-- THE SIGNATURE: one bar, one fact — how much of this inbox is
                         fully backfilled. A three-part ribbon was the first idea and was
                         cut: an empty channel can also be history-complete, so the
                         segments could contradict each other. -->
                    <div class="mt-1.5 flex items-center gap-1.5">
                      <div class="h-[3px] flex-1 overflow-hidden rounded-full bg-base-content/10">
                        <div
                          class="h-full rounded-full {pct === 100 ? 'bg-success' : 'bg-warning'}"
                          style="width:{pct}%"
                        ></div>
                      </div>
                      <span class="shrink-0 font-mono text-[0.55rem] tabular-nums text-base-content/45">{pct}%</span>
                    </div>

                    {#if row.issues.length}
                      <div class="mt-1.5 flex flex-wrap gap-1">
                        {#each row.issues as code (code)}
                          <span class="rounded-field bg-warning/15 px-1 py-px text-[0.55rem] font-medium leading-tight text-warning">
                            {issueText(code)}
                          </span>
                        {/each}
                      </div>
                    {/if}
                  </button>
                </li>
              {/each}
            </ul>
          {/if}
        </section>

        <!-- ═══ 2 + 3 · conversations and transcript ═════════════════════
             ONE COMPONENT, shared with the Helper's DM tab. The list, the search, the
             selection, the transcript, the scroll-up paging and the bubbles are all
             `@ui`'s `<Conversation>`; what stays on this page is the inbox picker to
             the left, which is the one thing a side panel has no use for — it reads a
             single account's own mirror and never chooses whose.

             It renders as two panes here because it is given the width for them, and
             as a pushed thread in a 340px panel, from the same measurement. This grid
             therefore hands it BOTH remaining columns rather than placing it in one. -->
        <!-- `--sg-conv-h` is how a host that has ALREADY measured its space tells the
             shared view to fill it. Without it the view falls back to 70vh, which on
             this page is ~125px short of the pane these three columns were measured
             into — dead space under a transcript, on the one screen laid out to give
             the transcript everything. The subtraction is the stats line above it. -->
        <div
          class="col-span-2 flex min-h-0 flex-col"
          style={paneH != null ? `--sg-conv-h:${paneH - 58}px` : undefined}
        >
          {#if !inbox}
            <div class="grid flex-1 place-items-center rounded-box border hair bg-base-100 px-6 text-center">
              <p class="text-sm text-base-content/62">Select an inbox.</p>
            </div>
          {:else}
            <!-- The per-inbox facts the picker cannot show, above the conversation view:
                 they describe the SET being listed, not any one row in it. -->
            <div class="mb-2 flex flex-wrap items-baseline gap-x-3 font-mono text-[0.6rem] tabular-nums text-base-content/55">
              <span>{channels.length} / {channelTotal.toLocaleString()} conversations</span>
              <span>{inbox.linked_channels} linked · {inbox.empty_channels} empty</span>
              {#if inbox.last_synced_at}<span>synced {timeAgo(inbox.last_synced_at, nowMs)}</span>{/if}
            </div>

            <Conversation
              conversations={asConversations}
              bind:openId
              bind:query={channelQuery}
              searchPlaceholder="Find a counterpart…"
              messages={threadMessages}
              hasOlder={msgOffset > 0}
              onLoadOlder={loadOlder}
              loading={channelsLoading}
              threadLoading={messagesLoading}
              threadHeader={channelHead}
              timestamp="exact"
              now={nowMs}
            >
              {#snippet header()}
                <label class="flex shrink-0 cursor-pointer select-none items-center gap-1.5 text-[0.68rem] text-base-content/72">
                  <input
                    type="checkbox" class="toggle toggle-xs" checked={issuesOnly}
                    onchange={(e) => { issuesOnly = (e.currentTarget as HTMLInputElement).checked; void loadChannels(); }}
                  />
                  Problems
                </label>
              {/snippet}

              {#snippet empty()}
                <StateBlock
                  state="empty"
                  message={issuesOnly ? 'Every conversation in this inbox looks healthy.' : 'No conversations.'}
                  hint={issuesOnly ? 'Turn off Problems to see them all.' : null}
                  compact
                />
              {/snippet}
            </Conversation>
          {/if}
        </div>
      </div>
    </PageContainer>
  {/if}
</AdminShell>
